import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import HeroSection from "@/pages/home/components/HeroSection";
import ServicesSection from "@/pages/home/components/ServicesSection";
import HowItWorksSection from "@/pages/home/components/HowItWorksSection";
import AdvantagesSection from "@/pages/home/components/AdvantagesSection";
import ContactSection from "@/pages/home/components/ContactSection";
import BookingSection from "@/pages/home/components/BookingSection";
import HotelSection from "@/pages/home/components/HotelSection";
import JobSection from "@/pages/home/components/JobSection";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://jekcleaning.com";

const schemaData = [
  {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "CleaningService"],
    "@id": `${SITE_URL}/#business`,
    "name": "Jek Cleaning Service",
    "description": "Servicio profesional de limpieza de casas, apartamentos, oficinas, hoteles y move in/move out en Jacksonville, FL. Personal verificado, productos seguros y garantía de satisfacción.",
    "telephone": "+1-904-608-7529",
    "email": "info@jekcleaning.com",
    "url": SITE_URL,
    "image": `${SITE_URL}/favicon.png`,
    "priceRange": "$$",
    "currenciesAccepted": "USD",
    "paymentAccepted": "Cash, Credit Card",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Jacksonville",
      "addressRegion": "FL",
      "addressCountry": "US",
      "postalCode": "32099"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 30.3322,
      "longitude": -81.6557
    },
    "areaServed": [
      { "@type": "City", "name": "Jacksonville" },
      { "@type": "State", "name": "Florida" }
    ],
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
        "opens": "08:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "09:00",
        "closes": "15:00"
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Servicios de Limpieza Jacksonville FL",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Limpieza de Casas Jacksonville FL",
            "description": "Servicio de limpieza residencial profesional para casas en Jacksonville, Florida."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Limpieza de Apartamentos Jacksonville",
            "description": "Limpieza profesional de apartamentos en Jacksonville, FL con personal verificado."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Limpieza de Oficinas Jacksonville",
            "description": "Servicio de limpieza comercial para oficinas y negocios en Jacksonville, Florida."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Move In / Move Out Cleaning Jacksonville",
            "description": "Limpieza profunda para mudanzas en Jacksonville, FL. Ideal para inquilinos y propietarios."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Limpieza Profesional para Hoteles Jacksonville",
            "description": "Servicio especializado de limpieza hotelera en Jacksonville, Florida."
          }
        }
      ]
    },
    "sameAs": []
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    "url": SITE_URL,
    "name": "Jek Cleaning Service",
    "description": "Limpieza profesional de casas, apartamentos, oficinas y hoteles en Jacksonville, FL",
    "inLanguage": "es-US",
    "publisher": {
      "@id": `${SITE_URL}/#business`
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${SITE_URL}/?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${SITE_URL}/#webpage`,
    "url": SITE_URL,
    "name": "Jek Cleaning Service Jacksonville FL | Limpieza Profesional de Casas y Apartamentos",
    "description": "Jek Cleaning Service en Jacksonville, FL ofrece limpieza profesional de casas, apartamentos, oficinas y move in/move out. Solicita tu cotización gratis.",
    "inLanguage": "es-US",
    "isPartOf": { "@id": `${SITE_URL}/#website` },
    "about": { "@id": `${SITE_URL}/#business` },
    "dateModified": "2026-04-08T00:00:00-04:00"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "¿Qué servicios de limpieza ofrece Jek Cleaning Service en Jacksonville, FL?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ofrecemos limpieza profesional de casas, apartamentos, oficinas, move in/move out y limpieza especializada para hoteles en Jacksonville, FL. Disponible de forma semanal, quincenal, mensual o como servicio único."
        }
      },
      {
        "@type": "Question",
        "name": "¿Cómo puedo agendar una cita de limpieza en Jacksonville?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Puedes agendar tu cita directamente en nuestro sitio web usando el formulario de reservación, o contactarnos por WhatsApp o teléfono al +1-904-608-7529. Respondemos en menos de 24 horas."
        }
      },
      {
        "@type": "Question",
        "name": "¿Ofrecen servicios de limpieza para hoteles en Jacksonville?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sí, contamos con personal especializado en limpieza hotelera. Atendemos habitaciones, áreas comunes, cocinas y más. Ofrecemos turnos flexibles y podemos integrarnos al equipo del hotel según sus necesidades."
        }
      },
      {
        "@type": "Question",
        "name": "¿El personal de Jek Cleaning Service está verificado?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sí, todo nuestro personal pasa por un proceso de verificación de antecedentes y está capacitado profesionalmente. Garantizamos un servicio de limpieza seguro y confiable en Jacksonville, FL."
        }
      },
      {
        "@type": "Question",
        "name": "¿Cuánto cuesta el servicio de limpieza en Jacksonville, FL?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Los precios varían según el tipo y tamaño del espacio. Ofrecemos planes desde limpieza básica hasta limpieza profunda. Solicita tu cotización gratuita en nuestro sitio web o llámanos al +1-904-608-7529."
        }
      },
      {
        "@type": "Question",
        "name": "¿Trabajan los fines de semana?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sí, atendemos los sábados de 9:00 AM a 3:00 PM. De lunes a viernes estamos disponibles de 8:00 AM a 6:00 PM para servicios de limpieza en Jacksonville, FL."
        }
      }
    ]
  }
];

export default function HomePage() {
  useEffect(() => {
    const existingScripts = document.querySelectorAll('script[data-schema="home"]');
    existingScripts.forEach((s) => s.remove());

    schemaData.forEach((schema, i) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-schema", "home");
      script.setAttribute("data-schema-index", String(i));
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema="home"]').forEach((s) => s.remove());
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        <HeroSection />
        <ServicesSection />
        <HowItWorksSection />
        <AdvantagesSection />
        <HotelSection />
        <BookingSection />
        <ContactSection />
        <JobSection />
      </main>
      <Footer />

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/1234567890"
        target="_blank"
        rel="nofollow noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center bg-green-500 hover:bg-green-400 text-white rounded-full transition-all duration-200 hover:scale-110 cursor-pointer"
        aria-label="Contactar por WhatsApp"
        title="Chatea con nosotros en WhatsApp"
      >
        <i className="ri-whatsapp-line text-2xl"></i>
      </a>
    </div>
  );
}
